import { ReactElement, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { HiOutlineChevronRight, HiOutlineBolt, HiOutlineCog, HiOutlinePlayCircle, HiOutlinePencil, HiOutlineChevronDown } from 'react-icons/hi2';
import { useNavigate, useLocation } from 'react-router-dom';
import { sortConfigsByLetter } from '../../../../utils/configSort';
import { numberToLetters } from '../../../../utils/letterNumber';
import CalibrationListGrid from '../../../CalibrationList/CalibrationListGrid';
import Button from '../../../../components/Button';
import ButtonList from '../../../../components/ButtonList';
import { useReprocess } from '../../../../hooks/useReprocess';
import {
  useInvalidateSession,
  useUpdateSession,
  useSetWheelAndReprocess,
  useAssignCalibrationParams,
  useAssignCrr,
} from '../../../../hooks/useSessions';
import { SessionAnalysisType } from '../../../../types/SessionAnalysisType';
import { useActionGuard } from '../../../../components/ActionGuard/useActionGuard';
import toast from 'react-hot-toast';
import { useGatesWMinRoute, useUpdateGate } from '../../../../hooks/useGates';
import { useRoutes } from '../../../../hooks/useRoutes';

const SHOW_STORAGE_KEY = 'calibration-show';
const SESSION_PARAMS_STORAGE_KEY = 'session-params-show';

// Calibration bar color-coding and status text
enum CalibrationSeverity {
  GRAY = 'gray',
  GREEN = 'green',
  YELLOW = 'yellow',
  RED = 'red',
}

const SEVERITY_CONFIG: Record<CalibrationSeverity, { bg: string; border: string; label: string; rank: number }> = {
  [CalibrationSeverity.GRAY]: { bg: '#f3f4f6', border: '#03365D', label: 'No Autocalibration', rank: 0 },
  [CalibrationSeverity.GREEN]: { bg: '#dcfce7', border: '#14532d', label: 'Autocalibration Good', rank: 1 },
  [CalibrationSeverity.YELLOW]: { bg: '#fefce8', border: '#ca8a04', label: 'Autocalibration Warning, expand for details', rank: 2 },
  [CalibrationSeverity.RED]: { bg: '#fef2f2', border: '#dc2626', label: 'Autocalibration Error, values out of range', rank: 3 },
};

const CP_CORR_DEFAULT = 1.2499;
const YAW_OFFSET_DEFAULT = 0.0;
const DEFAULT_EPSILON = 0.001;

function getCpCorrSeverity(v: number): CalibrationSeverity {
  if (v > 1.02 && v < 1.25) return CalibrationSeverity.GREEN;
  if ((v > 1.0 && v <= 1.02) || (v >= 1.25 && v < 1.5)) return CalibrationSeverity.YELLOW;
  return CalibrationSeverity.RED;
}

function getYawOffsetSeverity(v: number): CalibrationSeverity {
  const abs = Math.abs(v);
  if (abs < 4) return CalibrationSeverity.GREEN;
  if (abs <= 7) return CalibrationSeverity.YELLOW;
  return CalibrationSeverity.RED;
}

function getCalibrationBarSeverity(configs: any[]): { severity: CalibrationSeverity; tooltip: string } {
  if (!configs || configs.length === 0) {
    return { severity: CalibrationSeverity.GRAY, tooltip: SEVERITY_CONFIG[CalibrationSeverity.GRAY].label };
  }

  const allDefault = configs.every((c: any) => {
    const cp = c.cp_corr ?? CP_CORR_DEFAULT;
    const yaw = c.yaw_offset ?? YAW_OFFSET_DEFAULT;
    return Math.abs(cp - CP_CORR_DEFAULT) < DEFAULT_EPSILON && Math.abs(yaw - YAW_OFFSET_DEFAULT) < DEFAULT_EPSILON;
  });

  if (allDefault) {
    return { severity: CalibrationSeverity.GRAY, tooltip: SEVERITY_CONFIG[CalibrationSeverity.GRAY].label };
  }

  let worst: CalibrationSeverity = CalibrationSeverity.GREEN;
  const issues: string[] = [];

  configs.forEach((c: any) => {
    const label = `Config ${numberToLetters(c.letter_number ?? 0)}`;
    const cp = c.cp_corr ?? CP_CORR_DEFAULT;
    const yaw = c.yaw_offset ?? YAW_OFFSET_DEFAULT;

    const cpDefault = Math.abs(cp - CP_CORR_DEFAULT) < DEFAULT_EPSILON;
    const yawDefault = Math.abs(yaw - YAW_OFFSET_DEFAULT) < DEFAULT_EPSILON;
    if (cpDefault && yawDefault) return;

    const cpSev = cpDefault ? CalibrationSeverity.GREEN : getCpCorrSeverity(cp);
    const yawSev = yawDefault ? CalibrationSeverity.GREEN : getYawOffsetSeverity(yaw);

    if (SEVERITY_CONFIG[cpSev].rank > SEVERITY_CONFIG[worst].rank) worst = cpSev;
    if (SEVERITY_CONFIG[yawSev].rank > SEVERITY_CONFIG[worst].rank) worst = yawSev;

    if (cpSev === CalibrationSeverity.YELLOW) issues.push(`${label}: cp_corr ${cp.toFixed(4)} in marginal range`);
    if (cpSev === CalibrationSeverity.RED) issues.push(`${label}: cp_corr ${cp.toFixed(4)} out of range`);
    if (yawSev === CalibrationSeverity.YELLOW) {
      issues.push(`${label}: yaw_offset ${yaw.toFixed(2)} in marginal range (4\u20137)`);
    }
    if (yawSev === CalibrationSeverity.RED) {
      issues.push(`${label}: yaw_offset ${yaw.toFixed(2)} exceeds safe range (>7)`);
    }
  });

  const tooltip =
    issues.length > 0
      ? `${SEVERITY_CONFIG[worst].label}:\n${issues.join('\n')}`
      : SEVERITY_CONFIG[worst].label;

  return { severity: worst, tooltip };
}

const SessionCalibrations = (props: { session: any }): ReactElement => {
  const { session } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const invalidateSession = useInvalidateSession(session.id);
  const updateSession = useUpdateSession(session.id);
  const wheelAndReprocess = useSetWheelAndReprocess(session.id);
  const calibrationParams = useAssignCalibrationParams(session.id);
  const crrAssign = useAssignCrr(session.id);
  const updateGate = useUpdateGate();
  const { data: gatesList } = useGatesWMinRoute();

  // Check if velodrome session
  const isVelodromeSession =
    session?.analysis_type === SessionAnalysisType.LOOP_VELODROME_INDOORS ||
    session?.analysis_type === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;

  // Determine the routes query string based on the analysis type
  const routesQueryString = isVelodromeSession
    ? session?.analysis_type === SessionAnalysisType.LOOP_VELODROME_INDOORS
      ? 'scope=loop_velodrome_indoors'
      : 'scope=loop_velodrome_outdoors'
    : '';

  const { data: velodromeRoutes } = useRoutes(routesQueryString);

  // Get the selected velodrome route
  const selectedVelodromeRoute = useMemo(() => {
    if (!velodromeRoutes || !session?.route_id) return null;
    return velodromeRoutes.find((route) => route.id === session.route_id) ?? null;
  }, [velodromeRoutes, session?.route_id]);

  // Use reprocess hook
  const { handleReprocess, handleAutoCalculate, isProcessing, isAutoCalculating } = useReprocess();

  // Use action guard for reprocess confirmation
  const { open: openReprocessGuard, ActionGuard } = useActionGuard();

  // Filter configs to only show those with branch = true
  const configsWithBranch =
    session?.configs?.filter((config: any) => {
      if (!config) return false;

      // Check if branch field is true
      return config.branch === true;
    }) || [];

  // Compute calibration bar severity from cp_corr and yaw_offset across all visible configs
  const barSeverity = useMemo(() => getCalibrationBarSeverity(configsWithBranch), [configsWithBranch]);
  const barConfig = SEVERITY_CONFIG[barSeverity.severity];

  // Whether this is a tyre reference/comparison session (controls cda_ref column in grid)
  const isTyreSession =
    session?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
    session?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  // Collect all parameters that are creating branches (excluding baseline configs)
  const branchParameters = new Set<string>();
  configsWithBranch.forEach((config: any) => {
    // Skip baseline configs (letter_number 0)
    if (config.letter_number === 0) return;

    // Add parameters from manual and autocal lists
    if (config.parameters_manual) {
      config.parameters_manual.forEach((param: string) => branchParameters.add(param));
    }
    if (config.autocal_list) {
      config.autocal_list.forEach((param: string) => branchParameters.add(param));
    }
  });

  // Initialize show state from localStorage
  const [show, setShow] = useState(() => {
    const stored = localStorage.getItem(SHOW_STORAGE_KEY);
    return stored !== null ? JSON.parse(stored) : false;
  });

  // Update states when session changes
  useEffect(() => {
    if (session) {
      setElevationDifference(session.elevation_difference ?? null);
      setElevationAutomatic(!session.elevation_manual);
      setAirspeedRef(session.airspeed_ref ?? null);
      setTemperatureRef(session.temperature_ref ?? null);
    }
  }, [session?.elevation_difference, session?.elevation_manual, session?.airspeed_ref, session?.temperature_ref]);

  // Initialize session parameters show state from localStorage
  const [showSessionParams, setShowSessionParams] = useState(() => {
    const stored = localStorage.getItem(SESSION_PARAMS_STORAGE_KEY);
    return stored !== null ? JSON.parse(stored) : false;
  });

  const [isHidden, setIsHidden] = useState(false);
  const isHiddenRef = useRef(isHidden);
  const hideTimeoutRef = useRef<number | null>(null);
  const [elevationDifference, setElevationDifference] = useState<number | null>(session?.elevation_difference ?? null);
  const [isEditingElevation, setIsEditingElevation] = useState(false);
  const [elevationAutomatic, setElevationAutomatic] = useState<boolean>(!session?.elevation_manual);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditingAirspeed, setIsEditingAirspeed] = useState(false);
  const [isEditingTemperature, setIsEditingTemperature] = useState(false);
  const [airspeedRef, setAirspeedRef] = useState<number | null>(session?.airspeed_ref ?? null);
  const [temperatureRef, setTemperatureRef] = useState<number | null>(session?.temperature_ref ?? null);
  const [airspeedUnit, setAirspeedUnit] = useState<'ms' | 'kmh'>('ms');
  const [isUpdatingGate, setIsUpdatingGate] = useState(false);
  const [showGateMenu, setShowGateMenu] = useState(false);
  const gateMenuRef = useRef<HTMLDivElement>(null);
  const [showRouteMenu, setShowRouteMenu] = useState(false);
  const routeMenuRef = useRef<HTMLDivElement>(null);

  // Close gate menu on outside click
  useEffect(() => {
    if (!showGateMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (gateMenuRef.current && !gateMenuRef.current.contains(e.target as Node)) {
        setShowGateMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGateMenu]);

  // Close route menu on outside click
  useEffect(() => {
    if (!showRouteMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        routeMenuRef.current &&
        !routeMenuRef.current.contains(e.target as Node)
      ) {
        setShowRouteMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRouteMenu]);

  // Gate display logic
  const selectedGateId = session?.gates_id;
  // Use session.gates for route gradient data (embedded in session response)
  const sessionGate = session?.gates;
  // Use gatesList lookup for additional display fields not in session.gates
  const gatesListGate = useMemo(() => {
    if (!gatesList || !selectedGateId) return null;
    return gatesList.find((gate) => gate.id === selectedGateId) ?? null;
  }, [gatesList, selectedGateId]);
  // Prefer session.gates for route data, fall back to gatesList for display-only fields
  const selectedGate = sessionGate || gatesListGate;

  const gateDisplay = useMemo((): {
    primary: string;
    routeDescription: string;
    gateId: string;
    distance: string;
  } => {
    // Use gatesListGate for display fields (address, description, notes)
    // Use selectedGate (prefers session.gates) for route data
    const displayGate = gatesListGate || selectedGate;
    if (displayGate) {
      const primaryCandidate =
        displayGate.address?.trim() || displayGate.description?.trim() || displayGate.notes?.trim();
      const primary = primaryCandidate && primaryCandidate.length > 0 ? primaryCandidate : displayGate.id;
      const routeDescription = displayGate.route_description?.trim() || '';
      const distance = selectedGate?.route_distance ? `${selectedGate.route_distance.toFixed(0)}m` : '';
      return { primary, routeDescription, gateId: displayGate.id, distance };
    }

    if (selectedGateId) {
      return {
        primary: selectedGateId,
        routeDescription: '',
        gateId: selectedGateId,
        distance: '',
      };
    }

    return {
      primary: 'No gate selected',
      routeDescription: '',
      gateId: '',
      distance: '',
    };
  }, [selectedGate, gatesListGate, selectedGateId]);

  const setupGateCallback = () => {
    (window as any).__gateSelectCallback = (gateId: string) => {
      handleGateUpdate(gateId);
      delete (window as any).__gateSelectCallback;
    };
  };

  const setupSessionPickerCallback = (
    mode: 'gates' | 'route',
  ) => {
    (window as any).__sessionPickerCallback = async (
      selected: {
        gates_id: string | null;
        route_id: string | null;
      },
    ) => {
      try {
        const updates: Record<string, any> = {};
        if (mode === 'gates' && selected.gates_id) {
          updates.gates_id = selected.gates_id;
        } else if (mode === 'route') {
          if (selected.route_id) {
            updates.route_id = selected.route_id;
          }
          if (selected.gates_id) {
            updates.gates_id = selected.gates_id;
          }
        }
        if (Object.keys(updates).length > 0) {
          await updateSession.mutateAsync(updates);
          toast.success('Updated from session');
          invalidateSession();
        }
      } catch {
        toast.error('Failed to update from session');
      }
      delete (window as any).__sessionPickerCallback;
    };
  };

  const currentReturnPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;

  const handleEditGatePosn = () => {
    setShowGateMenu(false);
    const firstRun = session?.configs
      ?.flatMap((c: any) => c.runs || [])
      ?.find((r: any) => r?.id);
    if (!firstRun) {
      toast.error('No runs available to edit gate position');
      return;
    }
    navigate(
      `/sessions/${session.id}/runs/${firstRun.id}/3d-map`,
    );
  };

  const handleCreateNew = () => {
    setShowGateMenu(false);
    setupGateCallback();
    const currentGate = selectedGateId && gatesList
      ? gatesList.find((g: any) => g.id === selectedGateId)
      : undefined;
    navigate('/gate-from-run', {
      state: {
        returnTo: currentReturnPath,
        sessionId: session.id,
        existingGatesLat: currentGate?.gates_lat,
        existingGatesLon: currentGate?.gates_lon,
        hasCallback: true,
      },
    });
  };

  const handlePrevSession = () => {
    setShowGateMenu(false);
    setupGateCallback();
    const refLat = selectedGate?.gates_lat?.[0] ?? null;
    const refLon = selectedGate?.gates_lon?.[0] ?? null;
    navigate('/route-selection', {
      state: {
        returnTo: currentReturnPath,
        selectedGateId: selectedGateId ?? null,
        sessionId: session.id,
        hasCallback: true,
        hideCreateNew: true,
        proximityFilter:
          refLat != null && refLon != null
            ? { lat: refLat, lon: refLon, radiusMeters: 5000 }
            : null,
      },
    });
  };

  const handleClearRoute = async () => {
    setShowRouteMenu(false);
    try {
      await updateSession.mutateAsync({ route_id: null });
      toast.success('Route cleared');
      invalidateSession();
    } catch {
      toast.error('Failed to clear route');
    }
  };

  const handleRouteFromPrevSession = () => {
    setShowRouteMenu(false);
    navigate('/route-picker', {
      state: {
        returnTo: currentReturnPath,
        sessionId: session.id,
      },
    });
  };

  const handleGatesFromSessionList = () => {
    setShowGateMenu(false);
    setupSessionPickerCallback('gates');
    navigate('/session-picker', {
      state: {
        returnTo: currentReturnPath,
        sessionId: session.id,
        mode: 'gates',
      },
    });
  };

  const handleRouteFromSessionList = () => {
    setShowRouteMenu(false);
    setupSessionPickerCallback('route');
    navigate('/session-picker', {
      state: {
        returnTo: currentReturnPath,
        sessionId: session.id,
        mode: 'route',
      },
    });
  };

  // Direct function to handle gate update
  const handleGateUpdate = useCallback(
    async (gateId: string) => {
      if (isUpdatingGate) {
        return;
      }

      setIsUpdatingGate(true);
      try {
        await updateSession.mutateAsync({ gates_id: gateId });
        toast.success('Gate updated successfully');
        invalidateSession();
      } catch (error) {
        console.error('Error updating gate:', error);
        toast.error('Failed to update gate');
      } finally {
        setIsUpdatingGate(false);
      }
    },
    [updateSession, invalidateSession, isUpdatingGate],
  );

  const handleElevationSave = async () => {
    if (!session.gates_id) {
      toast.error('No gate selected');
      return;
    }
    try {
      await updateGate.mutateAsync({
        gateId: session.gates_id,
        data: {
          id: session.gates_id,
          elevation_difference: elevationDifference,
          elevation_manual: !elevationAutomatic,
        },
      });
      toast.success('Elevation difference updated');
      setIsEditingElevation(false);
      invalidateSession();
    } catch (error) {
      console.error('Error updating elevation:', error);
      toast.error('Failed to update elevation difference');
    }
  };

  const handleAirspeedSave = async () => {
    try {
      // Convert km/h back to m/s if needed before saving
      const valueToSave = airspeedRef != null && airspeedUnit === 'kmh' ? airspeedRef / 3.6 : airspeedRef;
      await updateSession.mutateAsync({
        airspeed_ref: valueToSave,
        airspeed_ref_value: valueToSave,
      });
      toast.success('Reference speed updated');
      setIsEditingAirspeed(false);
      setAirspeedUnit('ms'); // Reset to m/s after save
      invalidateSession();
    } catch (error) {
      console.error('Error updating reference speed:', error);
      toast.error('Failed to update reference speed');
    }
  };

  // Get display value for editing (no forced formatting)
  const getAirspeedEditValue = () => {
    if (airspeedRef == null) return '';
    return airspeedUnit === 'kmh' ? (airspeedRef * 3.6).toString() : airspeedRef.toString();
  };

  // Handle unit change - convert current value
  const handleAirspeedUnitChange = (unit: 'ms' | 'kmh') => {
    if (airspeedRef != null) {
      if (unit === 'kmh' && airspeedUnit === 'ms') {
        // Converting from m/s to km/h
        setAirspeedRef(airspeedRef * 3.6);
      } else if (unit === 'ms' && airspeedUnit === 'kmh') {
        // Converting from km/h to m/s
        setAirspeedRef(airspeedRef / 3.6);
      }
    }
    setAirspeedUnit(unit);
  };

  const handleTemperatureSave = async () => {
    try {
      const formattedTemp = temperatureRef != null ? parseFloat(temperatureRef.toFixed(1)) : null;
      await updateSession.mutateAsync({ temperature_ref: formattedTemp });
      toast.success('Reference temperature updated');
      setIsEditingTemperature(false);
      invalidateSession();
    } catch (error) {
      console.error('Error updating reference temperature:', error);
      toast.error('Failed to update reference temperature');
    }
  };

  const handleAutomaticToggle = async (checked: boolean) => {
    if (!session.gates_id) {
      toast.error('No gate selected');
      return;
    }
    setElevationAutomatic(checked);
    try {
      await updateGate.mutateAsync({
        gateId: session.gates_id,
        data: {
          id: session.gates_id,
          elevation_manual: !checked,
        },
      });
      toast.success('Elevation mode updated');
      invalidateSession();
    } catch (error) {
      console.error('Error updating elevation mode:', error);
      toast.error('Failed to update elevation mode');
      // Revert on error
      setElevationAutomatic(!checked);
    }
  };

  useEffect(() => {
    isHiddenRef.current = isHidden;
  }, [isHidden]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleScroll = () => {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      if (!isMobile) {
        if (isHiddenRef.current) {
          setIsHidden(false);
        }
        return;
      }

      // Hide while the user is actively scrolling.
      if (!isHiddenRef.current) {
        setIsHidden(true);
      }

      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }

      hideTimeoutRef.current = window.setTimeout(() => {
        setIsHidden(false);
      }, 1000);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Save 'show' state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(SHOW_STORAGE_KEY, JSON.stringify(show));
  }, [show]);

  // Save 'showSessionParams' state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(SESSION_PARAMS_STORAGE_KEY, JSON.stringify(showSessionParams));
  }, [showSessionParams]);

  // Transform configs to have the same structure as calibrations for the grid
  const transformedConfigs = configsWithBranch.map((config: any) => {
    const baseTransform = {
      id: config.id,
      description: config.description,
      letter: config.letter || numberToLetters(config.letter_number),
      // Map config fields to calibration fields
      wheel_circumference: config.wheel_circumference,
      cp_corr: config.cp_corr,
      yaw_offset: config.yaw_offset,
      combined_mass: config.combined_mass,
      crr: config.crr,
      elevation_difference: config.elevation_difference,
      power_meter_slope: config.power_meter_slope,
      cda_ref: config.cda_ref,
      airspeed_ref: config.airspeed_ref,
      airspeed_slope: config.airspeed_slope,
      xw_para_slope: config.xw_para_slope,
      // Additional fields from config
      chest_ref: config.chest_ref,
      head_ref: config.head_ref,
      chest_slope: config.chest_slope,
      head_slope: config.head_slope,
      xw_wheel_compensation: config.xw_wheel_compensation,
    };

    // If config is not baseline (has baseline field set), filter to show only changed parameters
    if (config.baseline !== null && config.baseline !== undefined) {
      const changedParameters = new Set([...(config.parameters_manual || []), ...(config.autocal_list || [])]);

      // If there are changed parameters, filter the transform to only include those
      if (changedParameters.size > 0) {
        const filteredTransform: any = {
          id: baseTransform.id,
          description: baseTransform.description,
          letter: baseTransform.letter,
        };

        // Only include parameters that have changed
        Object.keys(baseTransform).forEach((key) => {
          if (key === 'id' || key === 'description' || key === 'letter') return;
          if (changedParameters.has(key)) {
            filteredTransform[key] = baseTransform[key as keyof typeof baseTransform];
          }
        });

        return filteredTransform;
      }
    }

    // For baseline configs or when no specific changed parameters, return all fields
    return baseTransform;
  });

  // Sort by letter
  const sortedConfigs = sortConfigsByLetter(transformedConfigs);

  // Button handlers
  const autoCalculate = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    await handleAutoCalculate(session, invalidateSession);
  };

  const reprocessWithConfirmation = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    openReprocessGuard({
      message: 'This will reprocess all runs, are you sure?',
      actionFn: async () => {
        // Extract runs from session and pass directly to avoid state race condition
        const runsToReprocess = session.configs?.flatMap((config: any) => config.runs || []) || [];
        await handleReprocess(invalidateSession, runsToReprocess);
      },
    });
  };

  const handleSetCircumference = async () => {
    try {
      await wheelAndReprocess.mutateAsync();
      toast.success(
        'Wheel circumference set from catalog'
        + ' successfully',
      );
      invalidateSession();
    } catch (error) {
      console.error(
        'Error setting circumference:', error,
      );
      toast.error(
        'Failed to set wheel circumference'
        + ' from catalog',
      );
    }
  };

  const handleSetCalibrationParams = async () => {
    try {
      await calibrationParams.mutateAsync();
      toast.success(
        'Calibration parameters set successfully',
      );
      invalidateSession();
    } catch (error) {
      console.error(
        'Error setting calibration params:', error,
      );
      toast.error(
        'Failed to set calibration parameters',
      );
    }
  };

  const handleAssignCrr = async () => {
    try {
      await crrAssign.mutateAsync();
      toast.success('Crr assigned successfully');
      invalidateSession();
    } catch (error) {
      console.error('Error assigning Crr:', error);
      toast.error('Failed to assign Crr');
    }
  };

  return (
    <div
      className={`cr-fixed cr-bottom-0 cr-left-0 cr-right-0 cr-z-50 cr-w-full cr-transition-transform cr-duration-200
       md:cr-sticky md:cr-bottom-0 md:cr-left-auto md:cr-right-auto md:cr-mx-[-5%] ${
         isHidden ? 'cr-pointer-events-none cr-translate-y-full' : 'cr-translate-y-0'
       }`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Combined Calibration Parameters Section */}
      <div
        className="cr-flex cr-cursor-pointer cr-flex-row
                      cr-gap-2 cr-border-t-2 cr-border-solid
                      cr-p-5 cr-shadow-lg md:cr-rounded-t-lg md:cr-border-2 cr-transition-colors cr-duration-300"
        style={{ backgroundColor: barConfig.bg, borderColor: barConfig.border }}
        title={barSeverity.tooltip}
        onClick={() => {
          setShow(!show);
        }}
      >
        <HiOutlineChevronRight
          className={`cr-size-5 cr-stroke-[3px] cr-text-[#03365D]
                  cr-transition-transform cr-duration-[300ms] ${show && 'cr-rotate-90'}`}
        />
        <div className="cr-grow cr-text-center cr-font-main cr-text-[16px] cr-font-semibold" style={{ color: '#03365D' }}>
          Calibration Parameters – <span className="cr-italic cr-font-normal" style={{ fontSize: '0.9em' }}>{barConfig.label}</span>
        </div>
        <HiOutlineChevronRight
          className={`cr-size-5 cr-stroke-[3px] cr-text-[#03365D]
                    cr-transition-transform cr-duration-[300ms] ${show && 'cr-rotate-90'}`}
        />
      </div>
      {show && (
        <div className="cr-max-h-[60vh] cr-overflow-y-auto cr-border-solid cr-border-[#03365D] cr-bg-white md:cr-border-x-2">
          {/* Session Parameters Section */}
          <div className="cr-border-b cr-border-gray-200">
            <div
              className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2 cr-p-2 hover:cr-bg-gray-50"
              onClick={() => setShowSessionParams(!showSessionParams)}
            >
              <HiOutlineChevronRight
                className={`cr-size-4 cr-stroke-[2px] cr-transition-transform cr-duration-200 ${showSessionParams && 'cr-rotate-90'}`}
                style={{ color: '#03365D' }}
              />
              <h4 className="cr-text-sm cr-font-semibold" style={{ color: '#03365D' }}>Session Parameters</h4>
            </div>

            {showSessionParams && (
              <div className="cr-p-3 cr-pl-16">
                {/* Button Section */}
                <div className="cr-mb-3">
                  <ButtonList className="cr-gap-2">
                    <Button
                      onClick={() => setIsEditMode(!isEditMode)}
                      icon={<HiOutlinePencil className={'cr-size-5'} />}
                      className={`cr-px-3 cr-py-1.5 cr-text-sm !cr-normal-case !cr-font-bold ${isEditMode ? '!cr-bg-gray-100 !cr-text-[#03365D] !cr-border-gray-300' : '!cr-bg-gray-100 !cr-text-[#03365D] !cr-border-gray-300'}`}
                    >
                      {isEditMode ? 'Done' : 'Edit'}
                    </Button>
                    <Button
                      onClick={autoCalculate}
                      icon={<HiOutlineBolt className={'cr-size-5'} />}
                      disabled={isAutoCalculating}
                      className="cr-px-3 cr-py-1.5 cr-text-sm !cr-normal-case !cr-font-bold !cr-bg-gray-100 !cr-text-[#03365D] !cr-border-gray-300"
                    >
                      Auto Calibrate
                    </Button>
                    <Button
                      onClick={reprocessWithConfirmation}
                      icon={<HiOutlineCog className={'cr-size-5'} />}
                      disabled={isProcessing}
                      className="cr-px-3 cr-py-1.5 cr-text-sm !cr-normal-case !cr-font-bold !cr-bg-gray-100 !cr-text-[#03365D] !cr-border-gray-300"
                    >
                      Reprocess
                    </Button>
                    {/* Show tyre processing buttons only for specific session types */}
                    {(session.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
                      session.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON) && (
                      <>
                        <Button
                          onClick={handleSetCircumference}
                          disabled={updateSession.isPending}
                          icon={
                            <HiOutlinePlayCircle
                              className="cr-size-5"
                            />
                          }
                          className={
                            'cr-px-3 cr-py-1.5 cr-text-sm '
                            + '!cr-normal-case '
                            + '!cr-font-bold '
                            + '!cr-bg-gray-100 '
                            + '!cr-text-[#03365D] '
                            + '!cr-border-gray-300'
                          }
                        >
                          {updateSession.isPending
                            ? 'Processing...'
                            : 'Set circumference'}
                        </Button>
                        <Button
                          onClick={handleSetCalibrationParams}
                          disabled={updateSession.isPending}
                          icon={<HiOutlinePlayCircle className="cr-size-5" />}
                          className="cr-px-3 cr-py-1.5 cr-text-sm !cr-normal-case !cr-font-bold !cr-bg-gray-100 !cr-text-[#03365D] !cr-border-gray-300"
                        >
                          {updateSession.isPending ? 'Processing...' : 'Set calibration parameters'}
                        </Button>
                        <Button
                          onClick={handleAssignCrr}
                          disabled={updateSession.isPending}
                          icon={<HiOutlinePlayCircle className="cr-size-5" />}
                          className="cr-px-3 cr-py-1.5 cr-text-sm !cr-normal-case !cr-font-bold !cr-bg-gray-100 !cr-text-[#03365D] !cr-border-gray-300"
                        >
                          {updateSession.isPending ? 'Processing...' : 'Assign Crr'}
                        </Button>
                      </>
                    )}
                  </ButtonList>
                </div>

                {/* Gates and Elevation Difference Section - Hidden for Velodrome sessions */}
                {!isVelodromeSession && (
                  <div className="cr-mb-3 cr-grid cr-grid-cols-1 cr-gap-3 md:cr-grid-cols-2 cr-items-stretch">
                    {/* Gates Section */}
                    <div className="cr-flex cr-flex-col">
                      <div className="cr-mb-1 cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>Gates</div>
                      <div className="cr-flex cr-flex-1 cr-items-center cr-justify-between cr-gap-2 cr-rounded-md cr-border cr-border-gray-300 cr-bg-gray-50 cr-p-2">
                        <div className="cr-flex-1">
                          <p className="cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>{gateDisplay.primary}</p>
                          {gateDisplay.routeDescription && (
                            <p className="cr-text-xs" style={{ color: '#03365D' }}>{gateDisplay.routeDescription}</p>
                          )}
                          <div className="cr-flex cr-items-center cr-gap-3">
                            {gateDisplay.gateId ? (
                              <p className="cr-text-xs" style={{ color: '#03365D' }}>ID: {gateDisplay.gateId}</p>
                            ) : (
                              <p className="cr-text-xs" style={{ color: '#03365D' }}>Select a gate pair</p>
                            )}
                            {gateDisplay.distance && (
                              <p className="cr-text-xs" style={{ color: '#03365D' }}>Distance: {gateDisplay.distance}</p>
                            )}
                          </div>
                        </div>
                        {isEditMode && (
                          <div className="cr-flex cr-gap-1">
                            <div className="cr-relative" ref={gateMenuRef}>
                              <Button
                                onClick={() => setShowGateMenu((v) => !v)}
                                disabled={isUpdatingGate}
                                className="cr-px-2 cr-py-1 cr-text-sm"
                              >
                                {isUpdatingGate ? 'Updating...' : (
                                  <span className="cr-flex cr-items-center cr-gap-1">
                                    Edit Gates
                                    <HiOutlineChevronDown className="cr-size-3" />
                                  </span>
                                )}
                              </Button>
                              {showGateMenu && (
                                <div className="cr-absolute cr-right-0 cr-top-full cr-z-50 cr-mt-1 cr-w-44 cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white cr-py-1 cr-shadow-lg">
                                  <button
                                    type="button"
                                    onClick={handleEditGatePosn}
                                    className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-px-3 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                                  >
                                    Edit Position
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCreateNew}
                                    className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-px-3 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                                  >
                                    Create New
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handlePrevSession}
                                    className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-px-3 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                                  >
                                    Prev Session - Map
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleGatesFromSessionList}
                                    className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-px-3 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                                  >
                                    Prev Session - List
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="cr-relative" ref={routeMenuRef}>
                              <Button
                                onClick={() => setShowRouteMenu((v) => !v)}
                                className="cr-px-2 cr-py-1 cr-text-sm"
                              >
                                <span className="cr-flex cr-items-center cr-gap-1">
                                  Edit Route
                                  <HiOutlineChevronDown className="cr-size-3" />
                                </span>
                              </Button>
                              {showRouteMenu && (
                                <div className="cr-absolute cr-right-0 cr-top-full cr-z-50 cr-mt-1 cr-w-44 cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white cr-py-1 cr-shadow-lg">
                                  <button
                                    type="button"
                                    onClick={handleClearRoute}
                                    className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-px-3 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                                  >
                                    Clear
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleRouteFromPrevSession}
                                    className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-px-3 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                                  >
                                    Prev Session - Map
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleRouteFromSessionList}
                                    className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-px-3 cr-py-2 cr-text-left cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
                                  >
                                    Prev Session - List
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Route Gradient */}
                    <div className="cr-flex cr-flex-col">
                      <div className="cr-mb-1 cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>Route Gradient</div>
                      <div className="cr-flex-1 cr-rounded-md cr-border cr-border-gray-300 cr-bg-gray-50 cr-p-2">
                        <div className="cr-grid cr-grid-cols-4 cr-gap-4 cr-justify-items-start">
                          {/* First column - Bearing */}
                          <div className="cr-flex cr-flex-col cr-items-center">
                            {selectedGate?.route_bearing != null && (
                              <div className="cr-text-center">
                                <div className="cr-mb-1 cr-text-xs" style={{ color: '#03365D' }}>Bearing</div>
                                <span className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>
                                  {(() => {
                                    const bearing =
                                      selectedGate.route_bearing < 0
                                        ? selectedGate.route_bearing + 360
                                        : selectedGate.route_bearing;
                                    return `${bearing.toFixed(0)}°`;
                                  })()}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Second column - Percentage grade and distance */}
                          <div className="cr-flex cr-flex-col cr-items-center">
                            {session?.elevation_difference != null && selectedGate?.route_distance && (
                              <div className="cr-text-center">
                                <div className="cr-mb-1 cr-text-xs" style={{ color: '#03365D' }}>Grade</div>
                                <span
                                  className="cr-font-mono cr-text-sm"
                                  style={{ color: '#03365D' }}
                                >
                                  {((session.elevation_difference / selectedGate.route_distance) * 100).toFixed(2)}%
                                </span>
                                <div className="cr-font-mono cr-mt-1 cr-text-xs" style={{ color: '#03365D' }}>
                                  {Math.round(selectedGate.route_distance)}m
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Third column - Automatic checkbox and value */}
                          <div className="cr-flex cr-flex-col cr-items-center">
                            {/* Automatic checkbox */}
                            {isEditMode && (
                              <div className="cr-mb-1 cr-flex cr-items-center cr-gap-2">
                                <input
                                  type="checkbox"
                                  id="elevation-automatic"
                                  checked={elevationAutomatic}
                                  onChange={(e) => handleAutomaticToggle(e.target.checked)}
                                  className="cr-h-3 cr-w-3 cr-rounded cr-border-gray-300 cr-text-blue-600 focus:cr-ring-blue-500"
                                />
                                <label htmlFor="elevation-automatic" className="cr-text-xs" style={{ color: '#03365D' }}>
                                  Auto
                                </label>
                              </div>
                            )}

                            {/* Value display */}
                            <div className="cr-flex cr-flex-col cr-gap-1">
                              <div className="cr-mb-1 cr-text-xs" style={{ color: '#03365D' }}>Elevation Difference</div>
                              <span className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>
                                {session?.elevation_difference != null
                                  ? `${session.elevation_difference.toFixed(2)} m`
                                  : '-'}
                              </span>
                            </div>
                          </div>

                          {/* Fourth column - Edit controls or Edit button */}
                          <div className="cr-flex cr-flex-col cr-items-center">
                            {isEditingElevation ? (
                              <div className="cr-space-y-1">
                                <div className="cr-flex cr-items-center cr-gap-1">
                                  <input
                                    type="number"
                                    value={elevationDifference ?? ''}
                                    onChange={(e) =>
                                      setElevationDifference(e.target.value ? parseFloat(e.target.value) : null)
                                    }
                                    className="cr-flex-1 cr-rounded cr-border cr-border-gray-300 cr-px-1 cr-py-0.5 cr-text-xs"
                                    step="0.001"
                                  />
                                  <span className="cr-text-xs" style={{ color: '#03365D' }}>m</span>
                                </div>
                                <div className="cr-flex cr-gap-1">
                                  <Button
                                    onClick={handleElevationSave}
                                    disabled={updateGate.isPending}
                                    className="cr-flex-1 cr-px-1 cr-py-0.5 cr-text-xs"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setElevationDifference(session?.elevation_difference ?? null);
                                      setIsEditingElevation(false);
                                    }}
                                    className="cr-flex-1 cr-bg-gray-100 cr-px-1 cr-py-0.5 cr-text-xs"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="cr-flex cr-justify-end">
                                {isEditMode && !elevationAutomatic && (
                                  <Button
                                    onClick={() => setIsEditingElevation(true)}
                                    className="cr-px-2 cr-py-0.5 cr-text-xs"
                                  >
                                    Edit
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Velodrome Parameters Section - Only for Velodrome sessions */}
                {isVelodromeSession && (
                  <div className="cr-mb-3 cr-grid cr-grid-cols-1 cr-gap-3 md:cr-grid-cols-2 cr-items-stretch">
                    {/* Velodrome Info */}
                    <div className="cr-flex cr-flex-col">
                      <div className="cr-mb-1 cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>Velodrome</div>
                      <div className="cr-flex cr-flex-1 cr-items-center cr-rounded-md cr-border cr-border-gray-300 cr-bg-gray-50 cr-p-2">
                        <div className="cr-flex-1">
                          <p className="cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>
                            {selectedVelodromeRoute?.description || 'No velodrome selected'}
                          </p>
                          {selectedVelodromeRoute && (
                            <div className="cr-mt-1 cr-grid cr-grid-cols-3 cr-gap-4">
                              <div className="cr-text-center">
                                <div className="cr-text-xs" style={{ color: '#03365D' }}>Lap Length</div>
                                <span className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>
                                  {selectedVelodromeRoute.lap_length != null
                                    ? `${selectedVelodromeRoute.lap_length} m`
                                    : '-'}
                                </span>
                              </div>
                              <div className="cr-text-center">
                                <div className="cr-text-xs" style={{ color: '#03365D' }}>Bend Radius</div>
                                <span className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>
                                  {selectedVelodromeRoute.bend_radius != null
                                    ? `${selectedVelodromeRoute.bend_radius} m`
                                    : '-'}
                                </span>
                              </div>
                              <div className="cr-text-center">
                                <div className="cr-text-xs" style={{ color: '#03365D' }}>Bank Angle</div>
                                <span className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>
                                  {selectedVelodromeRoute.banking_angle != null
                                    ? `${selectedVelodromeRoute.banking_angle}°`
                                    : '-'}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Cornering Stiffness Ratio */}
                    <div className="cr-flex cr-flex-col">
                      <div className="cr-mb-1 cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>Cornering Stiffness Ratio</div>
                      <div className="cr-flex cr-flex-1 cr-items-center cr-rounded-md cr-border cr-border-gray-300 cr-bg-gray-50 cr-p-2">
                        <div className="cr-flex-1">
                          <span className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>
                            {session?.cornering_stiffness_ratio != null
                              ? `${session.cornering_stiffness_ratio.toFixed(2)} per deg.`
                              : session?.cornering_stiffness_ratio_default != null
                                ? `${session.cornering_stiffness_ratio_default.toFixed(2)} per deg. (default)`
                                : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reference Values Table */}
                <div className="cr-grid cr-grid-cols-2 cr-gap-3">
                  <div>
                    <div className="cr-mb-1 cr-flex cr-items-center cr-gap-2">
                      <div className="cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>Reference Speed</div>
                      {isEditMode && !isEditingAirspeed && (
                        <button
                          type="button"
                          onClick={() => {
                            setAirspeedRef(session?.airspeed_ref ?? null);
                            setAirspeedUnit('ms');
                            setIsEditingAirspeed(true);
                          }}
                          className="cr-rounded cr-p-1 hover:cr-bg-gray-200"
                          title="Edit Reference Speed"
                        >
                          <HiOutlinePencil className="cr-h-3 cr-w-3" style={{ color: '#03365D' }} />
                        </button>
                      )}
                    </div>

                    {/* Show radio buttons when in edit mode but not when actively editing */}
                    {isEditMode && !isEditingAirspeed && (
                      <div className="cr-mb-2 cr-flex cr-items-center cr-gap-2">
                        <label className="cr-flex cr-items-center cr-gap-1">
                          <input
                            type="radio"
                            name="airspeed-unit"
                            value="ms"
                            checked={airspeedUnit === 'ms'}
                            onChange={() => handleAirspeedUnitChange('ms')}
                            className="cr-h-3 cr-w-3"
                          />
                          <span className="cr-text-xs" style={{ color: '#03365D' }}>m/s</span>
                        </label>
                        <label className="cr-flex cr-items-center cr-gap-1">
                          <input
                            type="radio"
                            name="airspeed-unit"
                            value="kmh"
                            checked={airspeedUnit === 'kmh'}
                            onChange={() => handleAirspeedUnitChange('kmh')}
                            className="cr-h-3 cr-w-3"
                          />
                          <span className="cr-text-xs" style={{ color: '#03365D' }}>km/h</span>
                        </label>
                      </div>
                    )}

                    {isEditingAirspeed ? (
                      <div className="cr-flex cr-items-center cr-gap-1">
                        <input
                          type="number"
                          value={getAirspeedEditValue()}
                          onChange={(e) => setAirspeedRef(e.target.value ? parseFloat(e.target.value) : null)}
                          className="cr-flex-1 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm"
                          step="0.1"
                        />
                        <span className="cr-text-xs" style={{ color: '#03365D' }}>{airspeedUnit === 'ms' ? 'm/s' : 'km/h'}</span>
                        <Button
                          onClick={handleAirspeedSave}
                          disabled={updateSession.isPending}
                          className="cr-px-1 cr-py-0.5 cr-text-xs"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={() => {
                            setAirspeedRef(session?.airspeed_ref ?? null);
                            setAirspeedUnit('ms');
                            setIsEditingAirspeed(false);
                          }}
                          className="cr-bg-gray-100 cr-px-1 cr-py-0.5 cr-text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>
                        {airspeedUnit === 'ms'
                          ? session.airspeed_ref
                            ? session.airspeed_ref.toFixed(2)
                            : '-'
                          : session.airspeed_ref
                            ? `${(session.airspeed_ref * 3.6).toFixed(1)}`
                            : '-'}
                        {session.airspeed_ref && (
                          <span className="cr-ml-1 cr-text-xs" style={{ color: '#03365D' }}>
                            {airspeedUnit === 'ms' ? 'm/s' : 'km/h'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="cr-mb-1 cr-flex cr-items-center cr-gap-2">
                      <div className="cr-text-sm cr-font-medium" style={{ color: '#03365D' }}>Reference Temperature</div>
                      {isEditMode && !isEditingTemperature && (
                        <button
                          type="button"
                          onClick={() => setIsEditingTemperature(true)}
                          className="cr-rounded cr-p-1 hover:cr-bg-gray-200"
                          title="Edit Reference Temperature"
                        >
                          <HiOutlinePencil className="cr-h-3 cr-w-3" style={{ color: '#03365D' }} />
                        </button>
                      )}
                    </div>
                    {isEditingTemperature ? (
                      <div className="cr-flex cr-items-center cr-gap-1">
                        <input
                          type="number"
                          value={temperatureRef || ''}
                          onChange={(e) => setTemperatureRef(e.target.value ? parseFloat(e.target.value) : null)}
                          className="cr-flex-1 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm"
                          step="0.1"
                        />
                        <span className="cr-text-xs" style={{ color: '#03365D' }}>°C</span>
                        <Button
                          onClick={handleTemperatureSave}
                          disabled={updateSession.isPending}
                          className="cr-px-1 cr-py-0.5 cr-text-xs"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={() => {
                            setTemperatureRef(session?.temperature_ref ?? null);
                            setIsEditingTemperature(false);
                          }}
                          className="cr-bg-gray-100 cr-px-1 cr-py-0.5 cr-text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="cr-font-mono cr-text-sm" style={{ color: '#03365D' }}>{session.temperature_ref || '-'}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Calibration Parameters Grid - Only show if there are configs with branch = true */}
          {configsWithBranch.length > 0 && (
            <CalibrationListGrid
              calibrations={sortedConfigs}
              sessionId={session.id}
              branchParameters={Array.from(branchParameters)}
              isTyreSession={isTyreSession}
            />
          )}
        </div>
      )}
      <ActionGuard />
    </div>
  );
};
export default SessionCalibrations;
